import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GoogleMap from "../components/GoogleMaps.jsx";
import "../styles/Signup.css";
import "../styles/Common.css";
import "../styles/map.css";
import eyeOpen from "../assets/view.png";
import eyeClose from "../assets/hidden.png";

// Constants for better maintainability
const API_ENDPOINTS = {
  SEND_OTP: '/api/tour-operators/send-otp',
  VERIFY_OTP: '/api/tour-operators/verify-otp-and-create',
  RESEND_OTP: '/api/tour-operators/resend-otp',
  REVERSE_GEOCODE: '/api/maps/public/reverse-geocode'
};

const LOCAL_STORAGE_KEYS = {
  TEMP_OPERATOR_ID: 'tempOperatorId'
};

const VALIDATION = {
  PHONE_LENGTH: 10,
  PASSWORD_MIN_LENGTH: 8,
  OTP_LENGTH: 6,
  MAX_FILE_SIZE: 5 * 1024 * 1024 // 5MB
};

const BUSINESS_TYPES = [
  "Select Business Type",
  "Hotel",
  "HomeStay",
  "Resort",
  "Lodging And Accomodations",
  "Travel Agency"
];

const Signup = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessNumber: "",
    businessName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    address: "",
    businessType: BUSINESS_TYPES[0],
    description: "",
    socialLinks: "",
    website: "",
    languages: "",
    locationCoords: null,
    otp: ["", "", "", "", "", ""],
  });

  const [tempOperatorId, setTempOperatorId] = useState(null);
  const [brandingImageFile, setBrandingImageFile] = useState(null);
  const [brandingImagePreview, setBrandingImagePreview] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState({
    password: false,
    confirmPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpTimer, setOtpTimer] = useState(0);

  // Map states
  const [showMap, setShowMap] = useState(false);
  const [selectedCoords, setSelectedCoords] = useState(null);
  const [mapSearch, setMapSearch] = useState("");
  const [mapSuccess, setMapSuccess] = useState(null);

  const navigate = useNavigate();

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Clear any residual data when component mounts
  useEffect(() => {
    if (process.env.REACT_APP_NODE_ENV === 'development') {
      console.log('🔐 Signup page mounted - clearing residual data');
    }
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TEMP_OPERATOR_ID);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (brandingImagePreview) {
        URL.revokeObjectURL(brandingImagePreview);
      }
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TEMP_OPERATOR_ID);
    };
  }, [brandingImagePreview]);

  // OTP Timer Effect
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpTimer]);

  // Utility functions
  const capitalizeFirstLetter = useCallback((str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, []);

  const formatWebsiteUrl = useCallback((url) => {
    if (!url.trim()) return "";
    
    let formattedUrl = url.trim();
    formattedUrl = formattedUrl.replace(/^https?:\/\//i, '');
    
    if (formattedUrl && !formattedUrl.startsWith('http')) {
      if (formattedUrl.includes('.') && !formattedUrl.includes(' ')) {
        formattedUrl = 'https://' + formattedUrl;
      }
    }
    
    return formattedUrl;
  }, []);

  // Memoized input handlers
  const handleWebsiteChange = useCallback((e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      website: value,
    }));

    if (errors.website) {
      setErrors((prev) => ({
        ...prev,
        website: "",
      }));
    }
  }, [errors.website]);

  const handleInputChange = useCallback((e) => {
    const { id, value } = e.target;
    
    let formattedValue = value;
    
    if (['businessName', 'businessNumber', 'description', 'languages', 'socialLinks'].includes(id)) {
      formattedValue = capitalizeFirstLetter(value);
    }
    
    if (id === 'website') {
      handleWebsiteChange(e);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [id]: formattedValue,
    }));

    if (errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: "",
      }));
    }
  }, [capitalizeFirstLetter, handleWebsiteChange, errors]);

  const sanitizePhoneInput = useCallback((e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
    handleInputChange(e);
  }, [handleInputChange]);

  // Password strength calculator
  const calculatePasswordStrength = useCallback((password) => {
    let strength = 0;
    const checks = [
      password.length >= 8,
      /[A-Z]/.test(password),
      /[0-9]/.test(password),
      /[^A-Za-z0-9]/.test(password),
    ];

    strength = checks.filter(Boolean).length;

    return {
      width: strength * 25 + "%",
      className: ["", "weak", "medium", "medium", "strong"][strength] || "",
      text: [
        "Enter a password",
        "Weak password",
        "Medium strength",
        "Good password",
        "Strong password",
      ][strength] || "Enter a password",
    };
  }, []);

  // File handling
  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      if (file.size > VALIDATION.MAX_FILE_SIZE) {
        alert('Image size should be less than 5MB');
        return;
      }

      setBrandingImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setBrandingImagePreview(previewUrl);

      if (errors.brandingImage) {
        setErrors((prev) => ({
          ...prev,
          brandingImage: "",
        }));
      }
    }
  }, [errors.brandingImage]);

  const removeBrandingImage = useCallback(() => {
    setBrandingImageFile(null);
    setBrandingImagePreview(null);
    const fileInput = document.getElementById('branding');
    if (fileInput) fileInput.value = '';
  }, []);

  // Map location selection handler
  const handleMapLocationSelect = useCallback(async (coords) => {
    setSelectedCoords(coords);

    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.REVERSE_GEOCODE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ lat: coords.lat, lng: coords.lng }),
        }
      );

      const result = await response.json();
      
      let locationName = "";
      
      if (result.success && result.results && result.results.length > 0) {
        locationName = capitalizeFirstLetter(result.results[0].formatted_address);
        setMapSuccess(`Location selected: ${locationName}`);
      } else {
        locationName = `Coordinates: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
        setMapSuccess("Location selected - using coordinates");
      }

      setFormData((prev) => ({
        ...prev,
        address: locationName,
        locationCoords: coords,
      }));

      setTimeout(() => setMapSuccess(null), 3000);
      
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      const fallbackName = `Coordinates: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
      setFormData((prev) => ({
        ...prev,
        address: fallbackName,
        locationCoords: coords,
      }));
      setMapSuccess("Location selected (address lookup failed)");
      setTimeout(() => setMapSuccess(null), 3000);
    }
  }, [API_BASE_URL, capitalizeFirstLetter]);

  // Validation
  const validateStep1 = useCallback(() => {
    const newErrors = {};

    // Basic Info Validation
    if (!formData.businessNumber.trim()) {
      newErrors.businessNumber = "Business number is required";
    }

    if (!formData.businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < VALIDATION.PASSWORD_MIN_LENGTH) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Business Profile Validation
    if (!formData.address.trim()) {
      newErrors.address = "Address is required - Please select a location from the map";
    }

    if (formData.businessType === BUSINESS_TYPES[0]) {
      newErrors.businessType = "Please select a business type";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    if (!brandingImageFile) {
      newErrors.brandingImage = "Branding image is required";
    }

    if (formData.website.trim()) {
      const websiteRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
      if (!websiteRegex.test(formData.website)) {
        newErrors.website = "Please enter a valid website URL (e.g., uracab.com or https://uracab.com)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, brandingImageFile]);

  // OTP handlers
  const handleOtpChange = useCallback((index, value) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...formData.otp];
    newOtp[index] = value;
    setFormData((prev) => ({
      ...prev,
      otp: newOtp,
    }));

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp${index + 2}`);
      if (nextInput) nextInput.focus();
    }
  }, [formData.otp]);

  const handleOtpKeyDown = useCallback((index, e) => {
    if (e.key === "Backspace" && !formData.otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp${index}`);
      if (prevInput) prevInput.focus();
    }
  }, [formData.otp]);

  const togglePasswordVisibility = useCallback((field) => {
    setPasswordVisible((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  }, []);

  // API calls
  const sendOtp = useCallback(async () => {
    const formDataToSend = new FormData();
    
    // Basic Information
    formDataToSend.append('businessNumber', formData.businessNumber.trim());
    formDataToSend.append('businessName', formData.businessName.trim());
    formDataToSend.append('phone', formData.phone.trim());
    formDataToSend.append('email', formData.email.trim());
    formDataToSend.append('password', formData.password);
    
    // Business Profile
    formDataToSend.append('address', formData.address.trim());
    formDataToSend.append('businessType', formData.businessType);
    formDataToSend.append('description', formData.description.trim());
    
    if (formData.socialLinks.trim()) {
      formDataToSend.append('socialLinks', formData.socialLinks.trim());
    }
    
    if (formData.website.trim()) {
      const formattedWebsite = formatWebsiteUrl(formData.website);
      formDataToSend.append('website', formattedWebsite);
    }
    
    if (formData.languages.trim()) {
      formDataToSend.append('languages', formData.languages.trim());
    }
    
    if (formData.locationCoords) {
      formDataToSend.append('locationCoords', JSON.stringify(formData.locationCoords));
    }

    if (brandingImageFile) {
      formDataToSend.append('brandingImage', brandingImageFile);
    }

    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.SEND_OTP}`,
      {
        method: "POST",
        body: formDataToSend,
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to send OTP");
    }

    return result;
  }, [formData, brandingImageFile, formatWebsiteUrl, API_BASE_URL]);

  const verifyOtp = useCallback(async (otp) => {
    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.VERIFY_OTP}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone: formData.phone,
          otp: otp,
          tempOperatorId: tempOperatorId || localStorage.getItem(LOCAL_STORAGE_KEYS.TEMP_OPERATOR_ID)
        }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "OTP verification failed");
    }

    return result;
  }, [formData.phone, tempOperatorId, API_BASE_URL]);

  const resendOtp = useCallback(async () => {
    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.RESEND_OTP}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone: formData.phone,
          tempOperatorId: tempOperatorId || localStorage.getItem(LOCAL_STORAGE_KEYS.TEMP_OPERATOR_ID)
        }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to resend OTP");
    }

    return result;
  }, [formData.phone, tempOperatorId, API_BASE_URL]);

  // Form submissions
  const handleStep1Submit = useCallback(async (e) => {
    e.preventDefault();

    if (!validateStep1()) return;

    setIsLoading(true);

    try {
      const result = await sendOtp();
      
      if (process.env.REACT_APP_NODE_ENV === 'development') {
        console.log("OTP for development:", result.otpCode);
      }
      
      setTempOperatorId(result.tempOperatorId);
      localStorage.setItem(LOCAL_STORAGE_KEYS.TEMP_OPERATOR_ID, result.tempOperatorId);

      setCurrentStep(2);
      setOtpTimer(60);
      alert(`OTP sent to ${formData.phone}`);
    } catch (error) {
      console.error("OTP send error:", error);
      alert(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [validateStep1, sendOtp, formData.phone]);

  const handleStep2Submit = useCallback(async (e) => {
    e.preventDefault();
    const otp = formData.otp.join("");

    if (otp.length !== VALIDATION.OTP_LENGTH) {
      alert("Please enter a 6-digit OTP.");
      return;
    }

    setIsLoading(true);

    try {
      const result = await verifyOtp(otp);

      // Clean up
      if (brandingImagePreview) {
        URL.revokeObjectURL(brandingImagePreview);
      }
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TEMP_OPERATOR_ID);
      setTempOperatorId(null);

      if (process.env.REACT_APP_NODE_ENV === 'development') {
        console.log("✅ Account created successfully");
      }
      
      alert("Account created successfully! You can now log in.");
      navigate("/login", { replace: true });
      
    } catch (error) {
      console.error("OTP verification error:", error);
      alert(error.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [formData.otp, verifyOtp, brandingImagePreview, navigate]);

  const handleResendOtp = useCallback(async (e) => {
    e.preventDefault();

    if (otpTimer > 0) {
      alert(`Please wait ${otpTimer} seconds before resending OTP`);
      return;
    }

    setIsLoading(true);

    try {
      const result = await resendOtp();
      
      if (process.env.REACT_APP_NODE_ENV === 'development') {
        console.log("New OTP for development:", result.otpCode);
      }
      
      setOtpTimer(60);
      alert("OTP resent successfully.");
    } catch (error) {
      console.error("Resend OTP error:", error);
      alert(error.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [otpTimer, resendOtp]);

  const goBackFromSignup = useCallback(() => {
    if (currentStep === 1) {
      navigate("/login");
    } else {
      setCurrentStep(1);
    }
    setErrors({});
  }, [currentStep, navigate]);

  const passwordStrength = calculatePasswordStrength(formData.password);

  // Required field label component
  const RequiredLabel = useCallback(({ htmlFor, children }) => (
    <label htmlFor={htmlFor}>
      {children} <span className="required-asterisk">*</span>
    </label>
  ), []);

  return (
    <div className="signup-page">
      <div className="back-arrow" onClick={goBackFromSignup}>
        &larr;
      </div>

      <div className="form-container">
        {/* Step 1: All Details */}
        {currentStep === 1 && (
          <div className="step-content">
            <h2>Tour Operator Registration</h2>

            {mapSuccess && (
              <div className="upload-error success">
                <span>✅</span>
                <span>{mapSuccess}</span>
              </div>
            )}

            <form onSubmit={handleStep1Submit} className="signup-form">
              {/* Basic Information Section */}
              <div className="form-section">
                <h3>Basic Information</h3>
                
                <div className="input-group">
                  <RequiredLabel htmlFor="businessName">Business/Agency Name:</RequiredLabel>
                  <input
                    type="text"
                    id="businessName"
                    placeholder="Enter Business Name/Agency"
                    required
                    value={formData.businessName}
                    onChange={handleInputChange}
                    autoComplete="organization"
                    className={errors.businessName ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.businessName && (
                    <span className="error-text">{errors.businessName}</span>
                  )}
                </div>

                <div className="input-group">
                  <RequiredLabel htmlFor="businessNumber">Business/Agency Number:</RequiredLabel>
                  <input
                    type="text"
                    id="businessNumber"
                    placeholder="Enter Business ID"
                    required
                    value={formData.businessNumber}
                    onChange={handleInputChange}
                    autoComplete="username"
                    className={errors.businessNumber ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.businessNumber && (
                    <span className="error-text">{errors.businessNumber}</span>
                  )}
                </div>

                <div className="input-group">
                  <RequiredLabel htmlFor="phone">Phone Number:</RequiredLabel>
                  <input
                    type="tel"
                    id="phone"
                    maxLength="10"
                    placeholder="Enter Your Phone Number"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    onInput={sanitizePhoneInput}
                    autoComplete="tel"
                    className={errors.phone ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.phone && (
                    <span className="error-text">{errors.phone}</span>
                  )}
                </div>

                <div className="input-group">
                  <RequiredLabel htmlFor="email">Email:</RequiredLabel>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter Your Email Address"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="email"
                    className={errors.email ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <span className="error-text">{errors.email}</span>
                  )}
                </div>

                <div className="input-group">
                  <RequiredLabel htmlFor="password">Set Password:</RequiredLabel>
                  <div className="password-wrapper">
                    <input
                      type={passwordVisible.password ? "text" : "password"}
                      id="password"
                      placeholder="********"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      className={errors.password ? "error" : ""}
                      disabled={isLoading}
                    />
                    <img
                      className="toggle-eye"
                      src={passwordVisible.password ? eyeOpen : eyeClose}
                      alt={passwordVisible.password ? "Hide password" : "Show password"}
                      onClick={() => togglePasswordVisibility("password")}
                      loading="lazy"
                    />
                  </div>
                  <div className="password-strength">
                    <div
                      style={{ width: passwordStrength.width }}
                      className={passwordStrength.className}
                    ></div>
                  </div>
                  <div className="strength-text">{passwordStrength.text}</div>
                  {errors.password && (
                    <span className="error-text">{errors.password}</span>
                  )}
                </div>

                <div className="input-group">
                  <RequiredLabel htmlFor="confirmPassword">Re-Enter Password:</RequiredLabel>
                  <div className="password-wrapper">
                    <input
                      type={passwordVisible.confirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      placeholder="********"
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                      className={errors.confirmPassword ? "error" : ""}
                      disabled={isLoading}
                    />
                    <img
                      className="toggle-eye"
                      src={passwordVisible.confirmPassword ? eyeOpen : eyeClose}
                      alt={passwordVisible.confirmPassword ? "Hide password" : "Show password"}
                      onClick={() => togglePasswordVisibility("confirmPassword")}
                      loading="lazy"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <span className="error-text">{errors.confirmPassword}</span>
                  )}
                </div>
              </div>

              {/* Business Profile Section */}
              <div className="form-section">
                <h3>Business Profile</h3>

                <div className="input-group">
                  <RequiredLabel htmlFor="address">Address:</RequiredLabel>
                  <div className="location-search-container">
                    <button
                      type="button"
                      className="select-location-btn"
                      onClick={() => setShowMap(true)}
                      disabled={isLoading}
                    >
                      🗺️ Select Location On Map
                    </button>
                  </div>

                  <div className="address-display-container">
                    <input
                      type="text"
                      id="address"
                      placeholder="No location selected - Please select from map"
                      required
                      value={formData.address}
                      readOnly
                      className={`address-display ${errors.address ? "error" : ""} ${formData.address ? "has-value" : ""}`}
                    />
                  </div>
                  
                  {errors.address && (
                    <span className="error-text">{errors.address}</span>
                  )}

                  {formData.address && formData.locationCoords && (
                    <div className="selected-location-info">
                      <div className="location-coords">
                        <strong>Coordinates:</strong> Lat: {formData.locationCoords.lat.toFixed(6)}, Lng: {formData.locationCoords.lng.toFixed(6)}
                      </div>
                    </div>
                  )}
                </div>

                {showMap && (
                  <div className="map-modal-overlay">
                    <div className="map-modal">
                      <div className="map-modal-header">
                        <h3>Select Business Location</h3>
                        <button
                          className="close-map-btn"
                          onClick={() => setShowMap(false)}
                        >
                          ×
                        </button>
                      </div>

                      <div className="map-search-container">
                        <input
                          type="text"
                          placeholder="Search For A Location..."
                          className="map-search-input"
                          value={mapSearch}
                          onChange={(e) => setMapSearch(capitalizeFirstLetter(e.target.value))}
                        />
                      </div>

                      <div className="real-map-container">
                        <GoogleMap
                          onLocationSelect={handleMapLocationSelect}
                          selectedCoords={selectedCoords}
                          searchLocation={mapSearch}
                        />
                      </div>

                      <div className="map-modal-actions">
                        <button
                          onClick={() => setShowMap(false)}
                          className="confirm-location-btn"
                        >
                          Confirm Location
                        </button>
                        <button
                          onClick={() => setShowMap(false)}
                          className="cancel-btn"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="input-group">
                  <RequiredLabel htmlFor="businessType">Business Type:</RequiredLabel>
                  <select
                    id="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    className={errors.businessType ? "error" : ""}
                    disabled={isLoading}
                  >
                    {BUSINESS_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.businessType && (
                    <span className="error-text">{errors.businessType}</span>
                  )}
                </div>

                <div className="input-group">
                  <label htmlFor="website">
                    Website <span className="optional-text">(Recommended)</span>
                  </label>
                  <input
                    type="text"
                    id="website"
                    placeholder="Add Your Website To Help Customers Find You Online."
                    value={formData.website}
                    onChange={handleWebsiteChange}
                    autoComplete="url"
                    className={errors.website ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.website && (
                    <span className="error-text">{errors.website}</span>
                  )}
                </div>

                <div className="input-group">
                  <label htmlFor="languages">
                    Languages Spoken <span className="optional-text">(Recommended)</span>
                  </label>
                  <input
                    type="text"
                    id="languages"
                    placeholder="List Languages You Can Communicate In With Customers"
                    value={formData.languages}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                </div>

                <div className="input-group">
                  <RequiredLabel htmlFor="description">Description</RequiredLabel>
                  <textarea
                    id="description"
                    placeholder="Write Something About Your Business/Agency"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={errors.description ? "error" : ""}
                    disabled={isLoading}
                  ></textarea>
                  {errors.description && (
                    <span className="error-text">{errors.description}</span>
                  )}
                </div>

                <div className="input-group">
                  <label htmlFor="socialLinks">
                    Social Media Links <span className="optional-text">(Recommended)</span>
                  </label>
                  <input
                    type="text"
                    id="socialLinks"
                    value={formData.socialLinks}
                    onChange={handleInputChange}
                    autoComplete="url"
                    placeholder="Add Links To Your Social Media Profiles"
                    disabled={isLoading}
                  />
                </div>

                <div className="input-group">
                  <RequiredLabel htmlFor="branding">Branding Image</RequiredLabel>
                  <input 
                    type="file" 
                    id="branding" 
                    accept="image/*"
                    onChange={handleFileChange}
                    className={`file-input ${errors.brandingImage ? "error" : ""}`}
                    disabled={isLoading}
                  />
                  {brandingImagePreview && (
                    <div className="branding-preview">
                      <img 
                        src={brandingImagePreview} 
                        alt="Branding preview" 
                        className="preview-image"
                        loading="lazy"
                      />
                      <button 
                        type="button" 
                        className="remove-image-btn"
                        onClick={removeBrandingImage}
                        disabled={isLoading}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                  {errors.brandingImage && (
                    <span className="error-text">{errors.brandingImage}</span>
                  )}
                  <p className="file-help">Supported Formats: JPG, PNG, GIF, WebP. Max Size: 5MB</p>
                </div>
              </div>

              <div className="but-con">
                <button type="submit" className="submit-btn" disabled={isLoading}>
                  {isLoading ? "SENDING OTP..." : "SEND OTP"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: OTP Verification */}
        {currentStep === 2 && (
          <div className="step-content">
            <div className="otp-container">
              <h2>Enter OTP</h2>
              <p>An OTP Has Been Sent To Your Mobile Number</p>
              <form onSubmit={handleStep2Submit} className="otp-form">
                <div className="otp-inputs">
                  {formData.otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      id={`otp${index + 1}`}
                      maxLength="1"
                      className="otp-box"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      autoComplete="one-time-code"
                      aria-label={`OTP digit ${index + 1}`}
                      disabled={isLoading}
                    />
                  ))}
                </div>
                <div>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? "VERIFYING..." : "VERIFY"}
                  </button>
                </div>
              </form>
              <p className="resend-text">
                Didn't Receive The OTP?
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className={otpTimer > 0 ? "disabled resend-btn" : "resend-btn"}
                  disabled={otpTimer > 0 || isLoading}
                >
                  {otpTimer > 0 ? ` Resend In ${otpTimer}s` : " Resend"}
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(Signup);